import React, { useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { CreateSubtaskData } from '../../api/taskApi';
import { useCreateSubtasksBulk } from '../../hooks/useTasks';
import { formatEnumLabel } from '../../utils/organizationEnums';

const MAX_BULK_SUBTASKS = 50;

interface BulkSubtaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentTaskId: string;
  taskPriorities: string[];
  taskStatuses: string[];
  defaultPriority: string;
  defaultStatus: string;
  users?: Array<{ _id: string; firstName: string; lastName: string; email: string }>;
  onSuccess?: (count: number) => void;
}

const BulkSubtaskModal: React.FC<BulkSubtaskModalProps> = ({
  isOpen,
  onClose,
  parentTaskId,
  taskPriorities,
  taskStatuses,
  defaultPriority,
  defaultStatus,
  users,
  onSuccess,
}) => {
  const [bulkText, setBulkText] = useState('');
  const [priority, setPriority] = useState(defaultPriority);
  const [status, setStatus] = useState(defaultStatus);
  const [dueDate, setDueDate] = useState('');
  const [assignedToUser, setAssignedToUser] = useState('');

  const createBulkMutation = useCreateSubtasksBulk(parentTaskId);
  const toast = useToast();

  const parsedTitles = bulkText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const resetForm = () => {
    setBulkText('');
    setPriority(defaultPriority);
    setStatus(defaultStatus);
    setDueDate('');
    setAssignedToUser('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (parsedTitles.length === 0) {
      return;
    }

    const subtasks: CreateSubtaskData[] = parsedTitles.map((taskTitle) => ({
      taskTitle,
      description: taskTitle,
      priority,
      status,
      dueDate: dueDate || undefined,
      assignedToUser: assignedToUser ? [assignedToUser] : [],
    }));

    try {
      await createBulkMutation.mutateAsync(subtasks);
      onSuccess?.(subtasks.length);
      handleClose();
    } catch {
      toast({
        title: 'Error creating subtasks',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add subtasks in bulk</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Subtask titles</FormLabel>
              <Textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={'Design mockups\nWrite API endpoints\nQA review'}
                rows={8}
              />
              <FormHelperText>
                Enter one subtask per line. {parsedTitles.length} subtask
                {parsedTitles.length === 1 ? '' : 's'} ready (max {MAX_BULK_SUBTASKS}).
              </FormHelperText>
            </FormControl>

            <Text fontSize="sm" color="gray.600">
              These defaults apply to every subtask in this batch.
            </Text>

            <HStack spacing={4} align="flex-start">
              <FormControl>
                <FormLabel>Priority</FormLabel>
                <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                  {taskPriorities.map((value) => (
                    <option key={value} value={value}>
                      {formatEnumLabel(value)}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                  {taskStatuses.map((value) => (
                    <option key={value} value={value}>
                      {formatEnumLabel(value)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Due date</FormLabel>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </FormControl>

            <FormControl>
              <FormLabel>Assign to</FormLabel>
              <Select
                placeholder="Unassigned"
                value={assignedToUser}
                onChange={(e) => setAssignedToUser(e.target.value)}
              >
                {users?.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="purple"
            onClick={handleSubmit}
            isLoading={createBulkMutation.isPending}
            isDisabled={parsedTitles.length === 0 || parsedTitles.length > MAX_BULK_SUBTASKS}
          >
            Create {parsedTitles.length || ''} subtask{parsedTitles.length === 1 ? '' : 's'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkSubtaskModal;
